import React, { useState, useCallback, useEffect, useRef, useMemo, useContext } from "react";
import PropTypes from "prop-types";
import { Tabs, Tab, IconButton, Menu, MenuItem, Popper, Box } from "@mui/material";
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useDispatch, useSelector } from "react-redux";

import TabContainer from "./TabContainer";
import GridContainer from "../Grid/GridContainer";
import GridItem from "../Grid/GridItem";
import { NavContext } from '../../App';
import SAVIcon from "../IconComponents/SAVIcon";
import EditSAVTicket from "../../components/EditSAVTicket/EditSAVTicket";
import ViewSAVTicket from "../../Pages/SAV/TicketDetails/View/ViewSAVTicket";
import { removeTab, setLastActiveTab } from "../../ReduxTLK/features/Tabs/TabsSlice";
import ChevronRightIcon from "../IconComponents/ChevronRightIcon";
import ChevronLeftIcon from "../IconComponents/ChevronLeftIcon";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import H4 from "../TypoGraphy/H4";
import CloseIcon from '@mui/icons-material/Close';
import { Persistor } from "../..";
import { resetEditTicketDraftByIndex, resetNewTicketDraft } from "../../ReduxTLK/features/SAV/NewTicketDraftSlice";
import signalRService from "../Utility/SignalRConnection";
import New from "../../Pages/SAV/NewSAVTicket/NewSAVTicket";
import RedDotIcon from "../IconComponents/RedDotIcon";
import GenericDialogAddTicketSaveConfirmation from "../Dialog/GenericDialogAddTicketSaveConfirmation";
import GenericDialogEditTicketSaveConfirmation from "../Dialog/GenericDialogEditTicketSaveConfirmation";
import NavBarButtons from "../Navbar/NavBarButtons";
import styles from './tabs.module.css';

export default function TabsDemo({
    tabs,
    reloadTab,
    setTabs,
    setAddTab,
    selectedTab,
    onClose,
    tabsProps = { indicatorColor: "primary" },
    ...rest
}) {
    const dispatch = useDispatch();
    const context = useContext(NavContext);

    const [activetabs, setActiveTabs] = useState(context.tabs);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openCloseConfirmation, setOpenCloseConfirmation] = useState(false);
    const [openCloseEditConfirmation, setOpenCloseEditConfirmation] = useState(false);
    const [tabToBeDelete, setTabToBeDelete] = useState();
    const moreVertButtonRef = useRef(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleCloseMenu = () => setAnchorEl(null);

    const handleSelect = (selectedTabId) => {
        context.setActiveTab(selectedTabId);
        dispatch(setLastActiveTab(selectedTabId));
        if (selectedTabId !== 3 && selectedTabId !== 2) {
            context.setRoomId(selectedTabId?.substring(11));
            signalRService.onJoinRoom(selectedTabId?.substring(11));
        }
    };

    const opened_tabs = useSelector((state) => state.tabs.tabs);
    const last_opened = useSelector((state) => state.tabs.lastActiveTab);

    useEffect(() => {
        if (opened_tabs?.length > 0) {
            const t = opened_tabs?.map((tab) => {
                if (tab.id === 2) {
                    const existingAddTicket = tabs.findIndex((add) => add.id === 2);
                    if (existingAddTicket === -1) {
                        tab.component = <New />;
                        tab.closeable = true;
                        tab.icon = <SAVIcon />;
                        return tab;
                    }
                }
                if ((tab.module === 'SAV' && tab.action === 'Edit') || (tab.module === 'SAV' && tab.action === 'View')) {
                    const existingTabIndex = tabs.findIndex((existingTab) => existingTab.id === tab.id);
                    if (existingTabIndex === -1) {
                        return { ...tab, component: tab.action === 'Edit' ? <EditSAVTicket id={tab.identifier} /> : <ViewSAVTicket id={tab.identifier} />, closeable: true, icon: <SAVIcon /> };
                    }
                }
            });
            setActiveTabs([...tabs, ...t].filter((item) => item !== undefined));
            setTabs([...tabs, ...t].filter((item) => item !== undefined));
            if (last_opened === 3 || last_opened === 2) {
                context.setActiveTab(parseInt(last_opened));
                dispatch(setLastActiveTab(parseInt(last_opened)));
            } else {
                context.setActiveTab(last_opened ? last_opened : context.activeTab);
                dispatch(setLastActiveTab(last_opened ? parseInt(last_opened) : context.activeTab));
            }
        } else {
            setTabs(tabs);
        }
    }, []);

    const [lastOpenedTabId, setLastOpenedTabId] = useState(null);

    const newTicketDraft = useSelector((state) => state.draftNewTicket.newTicketDraft);
    const dirtyNewTicketDraft = useSelector((state) => state.draftNewTicket.dirty);

    const handleChange = useCallback((event, activeTab) => {
        if (lastOpenedTabId === 2) {
            Persistor.persist();
        }
        setLastOpenedTabId(context.activeTabRef.current);
        dispatch(setLastActiveTab(activeTab));
        context.setActiveTab(activeTab);
        Persistor.persist();
        if (activeTab !== 3 && activeTab !== 2) {
            context.setRoomId(activeTab?.substring(11));
            signalRService.onJoinRoom(activeTab?.substring(11));
        }
    }, [context, lastOpenedTabId, dispatch]);

    const editedTicketDraft = useSelector((state) => state.draftNewTicket.editedTicketDraft);
    const [dirtyTabState, setDirtyTabState] = useState(false);

    const isDirtyEditTab = (id) => {
        const dirtyEditTabIndex = editedTicketDraft.findIndex((item) => item.id === id);
        return dirtyEditTabIndex !== -1;
    };
    useEffect(() => {
        const updateDirtyTabState = async () => {
            const tabIndex = context.tabs.findIndex(tab => tab.id === context.activeTab);
            const tab = context.tabs.filter((tab, index) => index === tabIndex);
            if (tab && tab.length > 0 && tab[0]?.identifier !== undefined) {
                setDirtyTabState(isDirtyEditTab(tab[0].identifier));
            }
        };
        updateDirtyTabState();
    }, [context.tabs, context.activeTab, editedTicketDraft]);

    const handleClose = useCallback((event, tabToDelete) => {
        setTabToBeDelete(tabToDelete);
        Persistor.persist();
        event.stopPropagation();
        if (tabToDelete.id === 2) {
            if (dirtyNewTicketDraft) {
                setOpenCloseConfirmation(true);
                return;
            } else {
                const tabToDeleteIndex = context.tabs.findIndex(tab => tab.id === tabToDelete.id);
                const updatedTabs = context.tabs.filter((tab, index) => index !== tabToDeleteIndex);
                context.setTabs(updatedTabs);
                const previousTab =
                    context.tabs[tabToDeleteIndex - 1] ||
                    context.tabs[tabToDeleteIndex + 1] ||
                    {};
                setActiveTabs(updatedTabs);
                context.setActiveTab(previousTab.id);
                dispatch(setLastActiveTab(previousTab.id));
                dispatch(resetNewTicketDraft());
                dispatch(removeTab(tabToDelete));
                Persistor.persist();
                return;
            }
        } else {
            if (dirtyTabState) {
                setOpenCloseEditConfirmation(true);
                return;
            } else {
                const tabToDeleteIndex = context.tabs.findIndex(tab => tab.id === tabToDelete.id);
                const updatedTabs = context.tabs.filter((tab, index) => index !== tabToDeleteIndex);
                context.setTabs(updatedTabs);
                const previousTab =
                    context.tabs[tabToDeleteIndex - 1] ||
                    context.tabs[tabToDeleteIndex + 1] ||
                    {};
                if (previousTab.id !== 2 && previousTab.id !== 3) {
                    context.setRoomId(previousTab.id?.substring(11));
                    signalRService.onJoinRoom(previousTab.id?.substring(11));
                }
                setActiveTabs(updatedTabs);
                context.setActiveTab(previousTab.id);
                dispatch(setLastActiveTab(previousTab.id));
                dispatch(removeTab(tabToDelete));
                Persistor.persist();
            }
        }
    }, [context, dirtyNewTicketDraft, dirtyTabState, dispatch]);

    const [firstThreeTabs, remainingTabs] = useMemo(() => {
        return tabs.reduce((acc, tab) => {
            const [firstPart, secondPart] = acc;
            return tab.id === 3 ? [[...firstPart, tab], secondPart] : [firstPart, [...secondPart, tab]];
        }, [[], []]);
    }, [tabs]);

    return (
        <>
            <Box className={styles.tabBoxHeader}>
                <Tabs
                    value={context.activeTab}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    TabIndicatorProps={{ className: styles.tabIndicator }}
                    className={styles.tabsRoot}
                    ScrollButtonComponent={(props) => {
                        if (props.direction === "left" && !props.disabled) {
                            return (
                                <IconButton {...props} className={styles.tabScrollBtn}>
                                    <ChevronLeftIcon />
                                </IconButton>
                            );
                        } else if (props.direction === "right" && !props.disabled) {
                            return (
                                <>
                                    <IconButton {...props} className={styles.tabScrollBtn}>
                                        <ChevronRightIcon color="#007474" icon="arrow-right" />
                                    </IconButton>
                                    <Popper open={open} anchorEl={moreVertButtonRef.current} transition>
                                        {({ TransitionProps }) => (
                                            <PerfectScrollbar>
                                                <Menu
                                                    id="long-menu"
                                                    MenuListProps={{
                                                        'aria-labelledby': 'long-button',
                                                        ...TransitionProps,
                                                    }}
                                                    anchorOrigin={{
                                                        vertical: 'top',
                                                        horizontal: 'right',
                                                    }}
                                                    transformOrigin={{
                                                        vertical: 'top',
                                                        horizontal: 'right',
                                                    }}
                                                    open={open}
                                                    onClose={handleCloseMenu}
                                                    PaperProps={{ className: styles.tabMenuPaper }}
                                                >
                                                    <MenuItem><H4 title={'Pages ouvertes'}></H4></MenuItem>
                                                    {firstThreeTabs.map((option) => (
                                                        <MenuItem key={option.id} value={option.id} onClick={() => handleSelect(option.id)} className={styles.menuTabItem}>
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                                    {remainingTabs.map((option) => (
                                                        <MenuItem key={option.id} value={option.id} onClick={() => handleSelect(option.id)} className={styles.menuTabItem}>
                                                            <GridContainer display='flex' alignItems='space-between' direction='row'>
                                                                <GridItem sx={11} sm={11} md={11} xl={11} xxl={11}>
                                                                    {option.label}
                                                                </GridItem>
                                                                <GridItem sx={1} sm={1} md={1} xl={1} xxl={1}>
                                                                    <IconButton size="small" aria-label="close" onClick={event => handleClose(event, option)} className={styles.iconBtnClose}>
                                                                        <CloseIcon fontSize='small' />
                                                                    </IconButton>
                                                                </GridItem>
                                                            </GridContainer>
                                                        </MenuItem>
                                                    ))}
                                                </Menu>
                                            </PerfectScrollbar>
                                        )}
                                    </Popper>
                                    <IconButton
                                        ref={moreVertButtonRef}
                                        className={styles.tabMoreBtn}
                                        onClick={handleClick}
                                    >
                                        <MoreHorizIcon />
                                    </IconButton>
                                </>
                            );
                        } else {
                            return null;
                        }
                    }}
                >
                    {tabs && tabs.map(tab => (
                        <Tab
                            key={tab.id}
                            component={Box}
                            value={tab.id}
                            label={
                                typeof tab.label === "string" ? (
                                    <span className={styles.tabLabel}>
                                        {tab.label}
                                        {tab.closeable && (
                                            <IconButton
                                                component="div"
                                                onClick={event => handleClose(event, tab)}
                                                className={styles.iconBtnClose}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                        {tab.id === 2 && dirtyNewTicketDraft && (
                                            <IconButton component="div" className={styles.iconBtnRedDot}>
                                                <RedDotIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                        {tab.id !== 2 && tab.id !== 1 && isDirtyEditTab(tab.identifier) && (
                                            <IconButton component="div" className={styles.iconBtnRedDot}>
                                                <RedDotIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </span>
                                ) : null
                            }
                            className={[
                                styles.tabRoot,
                                context.activeTab === tab.id ? styles.tabActive : styles.tabInactive
                            ].join(' ')}
                            icon={tab.icon}
                            iconPosition={tab.id === 1 ? "center" : "start"}
                        />
                    ))}
                </Tabs>
                <Box className={styles.navbarButtonsRight}>
                    <NavBarButtons />
                </Box>
            </Box>
            <GenericDialogAddTicketSaveConfirmation
                open={openCloseConfirmation}
                setOpen={setOpenCloseConfirmation}
                tabToBeDelete={tabToBeDelete}
                setActiveTabs={setActiveTabs}
            />
            <GenericDialogEditTicketSaveConfirmation
                open={openCloseEditConfirmation}
                setOpen={setOpenCloseEditConfirmation}
                tabToBeDelete={tabToBeDelete}
                setActiveTabs={setActiveTabs}
            />
            {tabs.map(tab =>
                context.activeTab === tab.id ? (
                    <TabContainer key={tab.id}>
                        {tab.component}
                    </TabContainer>
                ) : null
            )}
        </>
    );
}

TabsDemo.propTypes = {
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
            id: PropTypes.number.isRequired,
            component: PropTypes.object.isRequired,
            closeable: PropTypes.bool.isRequired
        }).isRequired
    ).isRequired
};